import { Box, Divider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { Button } from '../../../../../Button';
import { ModalContent, ModalTitle } from '../../../../../Modal';
import { Alert } from '../../../../../Alert';
import { SessionDetailsHeader } from '../SessionDetailsHeader';
import { SectionPreviousSession, SectionPreviousSessionProps } from './SectionPreviousSession';
import { SectionContacts } from './SectionContacts';
import { SectionGeneral, SectionGeneralProps } from './SectionGeneral';
import { Appointment } from '@uplift/api';
import { AppointmentConflictAlert } from './AppointmentConflictAlert.component';


const useStyles = makeStyles()(theme => ({
  warningAlert: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  separatedItems: {
    '&>*:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  }
}));


export type SessionDetailsContentProps = {
  loading: boolean;
  onCancelSession: (appointment: Appointment) => void;

  appointment?: Appointment;
  patientDetails?: SectionGeneralProps['patientDetails'];

  onReviewNotes: SectionPreviousSessionProps['onReviewNotes'];
  previousAppointment?: SectionPreviousSessionProps['previousAppointment'];
};


export const SessionDetailsModalContent: React.VFC<SessionDetailsContentProps> = (props) => {
  const { classes } = useStyles();
  const isSignUpIncomplete = props.patientDetails && props.patientDetails?.status !== 'regular';

  return (
    <>
      <Box className={classes.separatedItems}>
        {props.appointment && (
          <AppointmentConflictAlert appointment={props.appointment} onCancelSession={props.onCancelSession} />
        )}
        {isSignUpIncomplete && (
          <Alert flat severity="warning" title="Signup Incomplete" className={classes.warningAlert}>
            You will not be able to start this session until the client finishes onboarding. Please<br /> contact the client to either complete necessary info today—or reschedule this session.
          </Alert>
        )}
      </Box>
      <SectionGeneral
        loading={props.loading}
        appointment={props.appointment}
        patientDetails={props.patientDetails}
      />
      <Divider />
      <SectionPreviousSession
        loading={props.loading}
        onReviewNotes={props.onReviewNotes}
        previousAppointment={props.previousAppointment}
      />
      <Divider />
      <SectionContacts
        loading={props.loading}
        isSignUpIncomplete={isSignUpIncomplete}
        patientDetails={props.patientDetails}
      />
    </>
  );
};


export const SessionDetailsGeneral: React.FC<SessionDetailsContentProps & {
  shown: boolean;
  onClose: () => void;
}> = (props) => {
  const l10n = useL10n();
  const isMobile = useIsMobile();

  const isCancelable = React.useMemo(() => {
    return props.appointment?.state && [ 'initial', 'scheduled', 'started', 'expired' ].includes(props.appointment?.state);
  }, [ props.appointment?.state ]);

  return (
    <>
      {isMobile ? (
        <ModalTitle onClose={props.onClose}>&nbsp;</ModalTitle>
      ) : (
        <SessionDetailsHeader
          onClose={props.onClose}
          onCancelSession={isCancelable ? props.onCancelSession : undefined}
          appointment={props.appointment}
        />
      )}
      <ModalContent noPadding dividers>
        <SessionDetailsModalContent {...props} />

        {isMobile && isCancelable && (
          <>
            <Divider />
            <Box p={2} display="flex" justifyContent="center">
              <Button onClick={props.onCancelSession} variant="outlined" color="secondary">{l10n.sessions.cancelSession}</Button>
            </Box>
          </>
        )}
      </ModalContent>
    </>
  );
};
